// Prevent duplicate class declaration
if (typeof CartPopupUpsells === 'undefined') {
  window.CartPopupUpsells = class extends HTMLElement {
  constructor() {
    super();
    
    this.overlay = null;
    this.activeElement = null;

    // Bind methods
    this.handleEscape = this.handleEscape.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    if (!this.querySelector('.cart-popup-upsells-wrapper')) {
      return;
    }
    
    this.setupEventListeners();
    this.createOverlay();
    
    // Auto-show if conditions are met with 2-second delay
    if (this.shouldShowPopup()) {
      setTimeout(() => {
        this.open();
      }, 2000);
    }
  }

  shouldShowPopup() {
    // Check if there are any upsell products to show
    const products = this.querySelectorAll('.cart-popup-upsells-single-product');
    
    // Also check if the wrapper exists (for testing purposes)
    const wrapper = this.querySelector('.cart-popup-upsells-wrapper');
    
    // Show if we have products OR if wrapper exists (for testing)
    return products.length > 0 || !!wrapper;
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'cart-popup-upsells-overlay';
    this.overlay.addEventListener('click', this.handleOverlayClick);
    document.body.appendChild(this.overlay);
  }

  setupEventListeners() {
    // Prevent form default submissions and handle with our custom handler
    const forms = this.querySelectorAll('form.product-form');
    forms.forEach(form => {
      form.addEventListener('submit', this.handleAddToCart);
    });

    // Variant selection dropdowns (if they exist)
    const variantSelects = this.querySelectorAll('.product-form__variants, select[name="id"]');
    variantSelects.forEach(select => {
      select.addEventListener('change', this.handleVariantChange);
    });

    // Option dropdowns
    const optionSelects = this.querySelectorAll('.cart-popup-upsells-single-product-single-option select');
    optionSelects.forEach(select => {
      select.addEventListener('change', this.handleOptionChange);
    });

    // Close buttons (both top and bottom)
    const closeButtons = this.querySelectorAll('[data-close-popup]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    
    // Show overlay
    if (this.overlay) {
      this.overlay.classList.add('active');
    }
    
    // Ensure element is rendered before animation
    requestAnimationFrame(() => {
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
    });
    
    // Add escape key listener
    document.addEventListener('keydown', this.handleEscape);
    
    // Focus trap
    setTimeout(() => {
      const focusElement = this.querySelector('.cart-popup-upsells-single-product-button button') || 
                           this.querySelector('[data-close-popup]');
      if (focusElement) {
        focusElement.focus();
      }
    }, 100);
  }

  close() {
    // Hide modal
    this.classList.remove('active');
    
    // Hide overlay
    if (this.overlay) {
      this.overlay.classList.remove('active');
    }
    
    // Restore body overflow
    document.body.classList.remove('overflow-hidden');
    
    // Remove escape key listener
    document.removeEventListener('keydown', this.handleEscape);
    
    // Return focus
    if (this.activeElement) {
      this.activeElement.focus();
      this.activeElement = null;
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  handleVariantChange(event) {
    const select = event.target;
    const form = select.closest('form');
    const isAvailable = select.selectedOptions[0]?.dataset.available === 'true';
    const button = form.querySelector('.cart-popup-upsells-single-product-button button');
    
    if (!button) return;
    
    const addToCartText = button.dataset.addToCartText || 'Add To Cart';
    const soldOutText = button.dataset.soldOutText || 'Sold Out';
    
    if (isAvailable) {
      button.textContent = addToCartText;
      button.disabled = false;
    } else {
      button.textContent = soldOutText;
      button.disabled = true;
    }
  }

  handleOptionChange(event) {
    const select = event.target;
    const form = select.closest('form');
    const variantSelect = form.querySelector('.product-form__variants');
    
    if (!variantSelect) return;
    
    // Build variant title from all option selects
    const optionSelects = form.querySelectorAll('.cart-popup-upsells-single-product-single-option select');
    const selectedValues = Array.from(optionSelects).map(s => s.value.trim());
    const variantTitle = selectedValues.join(' / ');
    
    // Find matching variant
    const matchingOption = Array.from(variantSelect.options).find(option => {
      return option.dataset.match === variantTitle;
    });
    
    if (matchingOption) {
      variantSelect.value = matchingOption.value;
      // Trigger change event to update button state
      variantSelect.dispatchEvent(new Event('change'));
    } else {
      // No matching variant found - disable button
      const button = form.querySelector('.cart-popup-upsells-single-product-button button');
      if (button) {
        button.textContent = button.dataset.soldOutText || 'Sold Out';
        button.disabled = true;
      }
    }
  }

  handleAddToCart(event) {
    event.preventDefault();

    const button = event.target;
    const form = button.closest('form');

    // Get variant ID from hidden input or select
    const variantInput = form.querySelector('input[name="id"]');
    const variantSelect = form.querySelector('select[name="id"]') || form.querySelector('.product-form__variants');
    const variantId = variantInput?.value || variantSelect?.value;

    // Show loading state
    button.classList.add('loading');
    button.disabled = true;
    const originalText = button.textContent;
    
    // Add to cart via fetch
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    
    // Add sections to update
    const cartDrawer = document.querySelector('cart-drawer');
    const mainCartItems = document.getElementById('main-cart-items');
    let sections = [];

    // Get cart drawer sections if available
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sections = cartDrawer.getSectionsToRender().map(section => section.id);
    }

    // Add main cart page sections if on cart page
    if (mainCartItems) {
      const mainCartSection = mainCartItems.dataset.id;
      if (!sections.includes(mainCartSection)) {
        sections.push(mainCartSection);
      }
      // Also add cart-icon-bubble if not already included
      if (!sections.includes('cart-icon-bubble')) {
        sections.push('cart-icon-bubble');
      }
    }

    if (sections.length > 0) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {

      // Show success state but keep button enabled and styled
      const addedText = button.dataset.addedText || 'Added!';
      button.textContent = addedText;
      button.classList.add('added');

      // Publish cart update event for theme integration
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-popup-upsells',
          productVariantId: variantId,
          cartData: data
        });
      }

      // Use cart drawer's renderContents method for proper updates
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      } else if (data.sections && cartDrawer) {
        this.updateCartSections(data.sections);
      }

      // Update cart count/icon
      this.updateCartIcon();
    })
    .catch(error => {
      // Restore button state
      button.classList.remove('loading');
      button.disabled = false;
      button.textContent = originalText;

      // Show error message
      alert('Sorry, there was an error adding this item to your cart. Please try again.');
    });
  }

  updateCartSections(sections) {
    if (!sections) return;

    try {
      // Update cart drawer contents
      const cartDrawerElement = document.querySelector('#CartDrawer');
      if (cartDrawerElement && sections['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const newContent = doc.querySelector('#CartDrawer');
        if (newContent) {
          cartDrawerElement.innerHTML = newContent.innerHTML;
        }
      }

      // Update main cart items (contains zapiet widget)
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        const mainCartSection = mainCartItems.dataset.id;
        if (sections[mainCartSection]) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections[mainCartSection], 'text/html');
          const newContent = doc.querySelector('.js-contents');
          const targetElement = mainCartItems.querySelector('.js-contents');
          if (newContent && targetElement) {
            targetElement.innerHTML = newContent.innerHTML;

            // Reinitialize Zapiet widget after DOM replacement
            this.reinitializeZapiet();
            document.dispatchEvent(new CustomEvent('cart-zapiet:init'));
          }
        }
      }

      // Update cart icon bubble
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (cartIcon && sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newIcon = doc.querySelector('#cart-icon-bubble');
        if (newIcon) {
          cartIcon.innerHTML = newIcon.innerHTML;
        }
      }
    } catch (error) {
      console.error('TEST:: Error updating cart sections:', error);
    }
  }

  reinitializeZapiet() {
    // Wait for DOM to be fully updated
    requestAnimationFrame(() => {
      const storePickupApp = document.getElementById('storePickupApp');
      if (!storePickupApp) {
        console.log('TEST:: storePickupApp element not found');
        return;
      }

      console.log('TEST:: Reinitializing Zapiet widget');

      // Retry logic to wait for Zapiet to be available
      let attempts = 0;
      const maxAttempts = 10;
      const attemptInterval = 200; // ms

      const tryReinitialize = () => {
        attempts++;

        if (window.Zapiet && typeof window.Zapiet.reinit === 'function') {
          console.log('TEST:: Calling Zapiet.reinit()');
          try {
            window.Zapiet.reinit();
          } catch (error) {
            console.error('TEST:: Error calling Zapiet.reinit:', error);
          }
        } else if (window.ZapietEvent) {
          // Zapiet is available but may not have a reinit method.
          // Force it to notice the new DOM by triggering a resize event.
          console.log('TEST:: ZapietEvent available, dispatching resize');
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
          }, 100);
        } else if (attempts < maxAttempts) {
          console.log(`TEST:: Zapiet not ready, retry ${attempts}/${maxAttempts}`);
          setTimeout(tryReinitialize, attemptInterval);
        } else {
          console.error('TEST:: Zapiet failed to initialize after', maxAttempts, 'attempts');
        }
      };

      tryReinitialize();
    });
  }

  updateCartIcon() {
    // Fallback: fetch cart data to update count
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
          element.style.display = cart.item_count > 0 ? 'block' : 'none';
        });
      })
      .catch(() => {
        // Error fetching cart data
      });
  }
}

}

// Define custom element (only if not already defined)
if (!customElements.get('cart-popup-upsells')) {
  customElements.define('cart-popup-upsells', window.CartPopupUpsells);
}

// Initialize if element exists
document.addEventListener('DOMContentLoaded', () => {
  // Wait for custom element to be defined
  customElements.whenDefined('cart-popup-upsells').then(() => {
    const upsellsElement = document.querySelector('#cart-popup-upsells');
    
    if (upsellsElement && !upsellsElement.closest('cart-popup-upsells')) {
      const wrapper = document.createElement('cart-popup-upsells');
      upsellsElement.parentNode.insertBefore(wrapper, upsellsElement);
      wrapper.appendChild(upsellsElement);
      
      // Force initialization if needed
      setTimeout(() => {
        // Try to manually trigger init if constructor wasn't called
        if (typeof wrapper.init === 'function') {
          wrapper.init();
        }
      }, 100);
    }
  }).catch(() => {
    // Error waiting for custom element
  });
});